import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableScreen} from '../../../../../../../components/paginated-table-screen/paginated-table-screen';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {useParams, useRouteMatch} from 'react-router-dom';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {UsersRepository} from '../../../../../../../repositories/users';
import EmailIcon from '@mui/icons-material/Email';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Email = 'email',
  AffiliationType = 'affiliation-type',
  State = 'state',
}

export default function EntityProfessionalCaregiversSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch caregivers
  const handleGetProfessionalCaregivers = async (limit: number, skip: number, search?: string) => {
    // Check entity id
    if (!id) {
      // Error
      throw new Error('Empty entity id.');
    }

    // Load caregivers
    const result: {totalCount: number; data: Array<AffiliatedUser>} =
      await UsersRepository.instance.getInEntity(id, limit, skip, UserType.Caregiver, search);

    // Process data
    const processedData: PaginatedTableData<AffiliatedUser> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.user.id || '',
        item: obj,
        enabled: obj.user.isActive,
        avatarUrl: obj.user.picture,
        toLink:
          url.charAt(url.length - 1) === '/' ? `${url}${obj.user.id}` : `${url}/${obj.user.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.user.name || '',
          },
          {
            columnId: ColumnIdentifier.Email,
            value: obj.user.email || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.user.isActive
              ? t(translations.entities.edit.professional_caregivers.state_value_true)
              : t(translations.entities.edit.professional_caregivers.state_value_false),
          },
          {
            columnId: ColumnIdentifier.AffiliationType,
            value: obj.affiliation.type
              ? t(translations.affiliation_types[obj.affiliation.type])
              : '',
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  return (
    <PaginatedTableScreen
      pageHeader={{
        searchLabel: t(translations.entities.edit.professional_caregivers.search_input_placeholder),
        rightButton: {
          label: t(translations.entities.edit.professional_caregivers.invite_button),
          toLink: url.charAt(url.length - 1) === '/' ? `${url}invite` : `${url}/invite`,
          endIcon: <EmailIcon />,
        },
      }}
      avatar={{type: 'user'}}
      emptyTableMessage={t(
        translations.entities.edit.professional_caregivers.no_professional_caregivers
      )}
      tableColumns={[
        {
          columnId: ColumnIdentifier.Name,
          title: t(translations.entities.edit.professional_caregivers.name_header),
          flexGrow: 2,
        },
        {
          columnId: ColumnIdentifier.AffiliationType,
          title: t(translations.entities.edit.professional_caregivers.affiliation_type_header),
          flexGrow: 1,
        },
        {
          columnId: ColumnIdentifier.Email,
          title: t(translations.entities.edit.professional_caregivers.email_header),
          flexGrow: 2,
        },
        {
          columnId: ColumnIdentifier.State,
          title: t(translations.entities.edit.professional_caregivers.state_header),
          flexGrow: 1,
        },
      ]}
      fetchData={handleGetProfessionalCaregivers}
      linkableRows={true}
    />
  );
}
